<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { LoadingOutlined, ExclamationCircleOutlined, EyeOutlined } from '@ant-design/icons-vue'
import type { MarkdownRenderer } from '../core/markdown-renderer'
import { setupCodeBlockActions } from '../core/code-block-actions'

const props = defineProps<{
  renderer: MarkdownRenderer | null
  markdown: string
}>()

const container = ref<HTMLElement | null>(null)
const html = ref('')
const loading = ref(false)
const error = ref('')

let renderVersion = 0
let currentTimer: ReturnType<typeof setTimeout> | null = null

async function doRender() {
  const version = ++renderVersion
  if (!props.renderer) return
  loading.value = true
  error.value = ''

  try {
    await props.renderer.initialize()
    if (version !== renderVersion) return
    html.value = await props.renderer.render(props.markdown)
  } catch (e) {
    if (version !== renderVersion) return
    error.value = e instanceof Error ? e.message : '渲染失败'
  } finally {
    if (version === renderVersion) {
      loading.value = false
    }
  }
}

function debouncedRender() {
  if (currentTimer) clearTimeout(currentTimer)
  currentTimer = setTimeout(() => doRender(), 200)
}

defineExpose({ render: doRender })

onMounted(() => {
  doRender()
})

onBeforeUnmount(() => {
  renderVersion++
  if (currentTimer) clearTimeout(currentTimer)
})

watch(() => props.markdown, () => debouncedRender())

watch(html, async () => {
  if (!container.value) return
  await nextTick()
  setupCodeBlockActions(container.value)
})
</script>

<template>
  <div class="markdown-renderer" ref="container">
    <div v-if="loading" class="markdown-renderer__state">
      <loading-outlined spin />
      <span>渲染中...</span>
    </div>
    <div v-else-if="error" class="markdown-renderer__error">
      <exclamation-circle-outlined :style="{ fontSize: '16px' }" />
      <div>
        <p>渲染出错：</p>
        <pre>{{ error }}</pre>
      </div>
    </div>
    <div
      v-else-if="html"
      class="markdown-renderer__content"
      v-html="html"
    ></div>
    <div v-else class="markdown-renderer__empty" @click="doRender">
      <eye-outlined :style="{ fontSize: '48px', color: 'var(--color-text-tertiary)' }" />
      <p>暂无内容，开始输入吧</p>
    </div>
  </div>
</template>

<style scoped>
.markdown-renderer {
  padding: 20px;
  max-width: 100%;
  overflow: auto;
}

.markdown-renderer__content {
  max-width: 100%;
  color: var(--color-text);
  word-wrap: break-word;
}

.markdown-renderer :deep(h1),
.markdown-renderer :deep(h2),
.markdown-renderer :deep(h3),
.markdown-renderer :deep(h4),
.markdown-renderer :deep(h5),
.markdown-renderer :deep(h6) {
  margin-bottom: 0.5em;
  line-height: 1.3;
  font-weight: 600;
}

.markdown-renderer :deep(h1) { font-size: 2em; }
.markdown-renderer :deep(h2) { font-size: 1.6em; border-bottom: 1px solid var(--color-border); padding-bottom: 0.3em; }
.markdown-renderer :deep(h3) { font-size: 1.35em; }

.markdown-renderer :deep(p) { margin: 0.5em 0; }

.markdown-renderer :deep(ul),
.markdown-renderer :deep(ol) { padding-left: 1.6em; margin: 0.5em 0; }

.markdown-renderer :deep(li) { margin: 0.2em 0; }

.markdown-renderer :deep(li.task-list-item) { list-style: none; display: flex; align-items: center; gap: 6px; }

.markdown-renderer :deep(code) {
  font-family: "JetBrains Mono", Consolas, monospace;
  font-size: 0.9em;
  background: var(--color-bg-tertiary);
  border-radius: 4px;
  padding: 0.1em 0.3em;
}

.markdown-renderer :deep(pre) {
  border-radius: 8px;
  overflow: auto;
}

.code-block__gutter :deep(pre) {
  border-radius: 8px;
  padding: 8px 0 0 8px;
  overflow: auto;
}

.markdown-renderer :deep(pre code) {
  background: transparent;
  padding: 0;
}

.markdown-renderer :deep(blockquote) {
  border-left: 4px solid var(--color-primary);
  background: var(--color-bg-secondary);
  padding: 0.5em 1em;
  margin: 1em 0;
  border-radius: 0 8px 8px 0;
  color: var(--color-text-secondary);
}

.markdown-renderer :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1em 0;
}

.markdown-renderer :deep(th),
.markdown-renderer :deep(td) {
  border: 1px solid var(--color-border);
  padding: 0.5em 1em;
}

.markdown-renderer :deep(th) { background: var(--color-bg-secondary); }

.markdown-renderer :deep(a) { color: var(--color-primary); text-decoration: underline; }

.markdown-renderer :deep(img) { max-width: 100%; border-radius: 8px; }

.markdown-renderer :deep(.mathjax-block) { overflow: hidden; margin: 1em 0; }

.markdown-renderer :deep(.typst-block) { margin: 1em 0; }

.markdown-renderer :deep(.typst-block svg) { max-width: 100%; height: auto; }

.markdown-renderer__state,
.markdown-renderer__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: var(--color-text-tertiary);
}

.markdown-renderer__error {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #fff1f0;
  border: 1px solid #ffa39e;
  border-radius: 8px;
  color: #cf1322;
}

.markdown-renderer__error pre {
  white-space: pre-wrap;
  margin-top: 8px;
  font-family: monospace;
}
</style>